import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { api, card, errMsg, input, money, pct } from './costingApi';

const KIND = { advance: ['Advance', 'bg-sky-700'], repayment: ['Repayment', 'bg-emerald-700'], acquisition: ['Vehicle paid by JSM', 'bg-indigo-700'], adjustment: ['Adjustment', 'bg-amber-700'] };

const AdjustmentForm = ({ onSaved }) => {
  const [f, setF] = useState({ date: new Date().toISOString().slice(0, 10), amount: '', reason: '' });
  const save = async () => {
    try { await api.post('/funding/adjustments', f); toast.success('Adjustment posted'); setF({ ...f, amount: '', reason: '' }); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap gap-2 items-end" data-testid="funding-adjustment-form">
      <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={`${input} w-40`} />
      <Input type="number" step="0.01" placeholder="+ owed more / − owed less" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={`${input} w-52`} data-testid="funding-adj-amount" />
      <Input placeholder="Reason (verified adjustment)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className={`${input} w-72`} data-testid="funding-adj-reason" />
      <Button onClick={save} disabled={!f.amount || !f.reason} className="bg-amber-600 hover:bg-amber-700" data-testid="funding-adj-save">Post adjustment</Button>
    </div>
  );
};

export const FundingPanel = () => {
  const [d, setD] = useState(null);
  const [range, setRange] = useState({ start: '', end: '' });
  const load = useCallback(() => api.get('/funding', { start: range.start || undefined, end: range.end || undefined }).then(setD).catch((e) => toast.error(errMsg(e))), [range]);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (r, status) => {
    const note = status === 'excluded' ? window.prompt('Why is this not a JSM Trading funding record? (e.g. fuel payment)') : '';
    if (status === 'excluded' && !note) return;
    try { await api.put(`/funding/${r.kind}/${r.id}/status`, { status, note }); toast.success(status === 'excluded' ? 'Excluded and reversed' : 'Verified'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <p className="text-slate-400 text-sm">Loading…</p>;
  const t = d.totals;
  const tiles = [
    ['Advances received', money(t.advances_received)], ['Vehicles paid by JSM', money(t.acquisitions_paid_by_jsm)], ['Principal repaid', money(t.principal_repayments)],
    ['Adjustments', money(t.adjustments)], ['Outstanding owed to JSM Trading', money(t.outstanding)], ['Repaid', pct(t.repayment_pct)],
  ];
  return (
    <div className="space-y-4" data-testid="funding-panel">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {tiles.map(([l, v]) => <Card key={l} className={card}><CardContent className="p-3"><p className="text-[11px] text-slate-400">{l}</p><p className="font-bold" data-testid={`funding-tile-${l.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{v}</p></CardContent></Card>)}
      </div>
      <p className="text-[11px] text-slate-400">Outstanding = advances (incl. vehicles JSM Trading paid for directly) − principal repayments ± verified adjustments. Ledger balance: {money(t.ledger_outstanding)}. Repayments are never COGS or expenses; no interest is charged.</p>
      {d.review_suggested > 0 && <p className="text-xs text-amber-400" data-testid="funding-review-note">{d.review_suggested} repayment(s) mention fuel — check they really were loan repayments, and exclude them if not.</p>}
      <AdjustmentForm onSaved={load} />
      <div className="flex gap-2 items-center text-sm">
        <span className="text-slate-400">History from</span>
        <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className={`${input} w-40`} data-testid="funding-start" />
        <span className="text-slate-400">to</span>
        <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className={`${input} w-40`} data-testid="funding-end" />
        <span className="text-xs text-slate-400">Period: +{money(d.period.advances)} / −{money(d.period.repayments)}</span>
      </div>
      <div className="overflow-x-auto border border-slate-700 rounded">
        <table className="w-full text-xs">
          <thead className="bg-slate-800 text-slate-300"><tr>{['Date', 'Type', 'Description', 'Source / paid to', 'Reference', 'Amount', 'Balance owed', 'Status', ''].map((h) => <th key={h} className="p-2 text-left">{h}</th>)}</tr></thead>
          <tbody>{d.transactions.map((r) => (
            <tr key={`${r.kind}-${r.id}`} className={`border-t border-slate-700 ${r.status === 'excluded' ? 'opacity-50' : ''}`} data-testid={`funding-row-${r.id}`}>
              <td className="p-2 whitespace-nowrap">{r.date}</td>
              <td className="p-2"><Badge className={KIND[r.kind][1]}>{KIND[r.kind][0]}</Badge></td>
              <td className="p-2">{r.description}{r.review_suggested && <Badge className="ml-1 bg-amber-600 text-[9px]">fuel?</Badge>}</td>
              <td className="p-2">{r.source}</td><td className="p-2 font-mono">{r.reference}</td>
              <td className={`p-2 text-right ${r.kind === 'repayment' ? 'text-emerald-400' : ''}`}>{r.kind === 'repayment' ? '−' : ''}{money(r.amount)}</td>
              <td className="p-2 text-right">{money(r.running_balance)}</td>
              <td className="p-2 capitalize">{r.status}</td>
              <td className="p-2">{['advance', 'repayment'].includes(r.kind) && (r.status === 'excluded'
                ? <Button size="sm" variant="ghost" className="h-6 text-xs text-sky-400" onClick={() => setStatus(r, 'verified')}>Re-include</Button>
                : <Button size="sm" variant="ghost" className="h-6 text-xs text-rose-400" onClick={() => setStatus(r, 'excluded')} data-testid={`funding-exclude-${r.id}`}>Exclude</Button>)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
};
